import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { leadsApi, LeadRecord, LeadCreditCheckSummary, LeadAmlSummary } from '../api/leads';
import './Tasks.css';

const ITEMS_PER_PAGE = 50;

const formatDateTime = (value?: string) => {
  if (!value) return 'N/A';
  try {
    return new Date(value).toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return value;
  }
};

const toDisplay = (value?: string) => value?.trim() || '—';

const formatIndividualName = (individual: any) => {
  if (!individual) return 'N/A';
  const parts = [individual.Title, individual.Firstname, individual.Surname]
    .map((part?: string) => (part || '').trim())
    .filter(Boolean);
  return parts.length ? parts.join(' ') : 'N/A';
};

export const Leads: React.FC = () => {
  const [leads, setLeads] = useState<LeadRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState<LeadRecord | null>(null);
  const [cancellingLeadId, setCancellingLeadId] = useState<string | null>(null);

  const [creditModalOpen, setCreditModalOpen] = useState(false);
  const [creditSummary, setCreditSummary] = useState<LeadCreditCheckSummary | null>(null);
  const [creditLoading, setCreditLoading] = useState(false);
  const [creditError, setCreditError] = useState<string | null>(null);

  const [amlModalOpen, setAmlModalOpen] = useState(false);
  const [amlSummary, setAmlSummary] = useState<LeadAmlSummary | null>(null);
  const [amlLoading, setAmlLoading] = useState(false);
  const [amlError, setAmlError] = useState<string | null>(null);

  const latestRequestIdRef = useRef(0);
  const initialisedRef = useRef(false);

  const loadLeads = useCallback(
    async (targetPage: number = 1, showSearchIndicator: boolean = false) => {
      const requestId = ++latestRequestIdRef.current;
      if (showSearchIndicator) {
        setIsSearching(true);
      }
      setLoading(true);

      try {
        const response = await leadsApi.listLeads({
          searchTerm: searchTerm || undefined,
          page: targetPage,
          pageSize: ITEMS_PER_PAGE,
          sortBy: 'createdAt',
          sortOrder: 'desc',
        });

        if (requestId !== latestRequestIdRef.current) {
          return;
        }

        setLeads(response.leads);
        setPage(response.currentPage);
        setTotalPages(response.totalPages || 1);
        setTotalCount(response.totalCount);
      } catch (error) {
        if (requestId !== latestRequestIdRef.current) {
          return;
        }
        console.error('Failed to load leads', error);
        setMessage({ type: 'error', text: 'Failed to load leads' });
      } finally {
        if (requestId === latestRequestIdRef.current) {
          setLoading(false);
          if (showSearchIndicator) {
            setIsSearching(false);
          }
        }
      }
    },
    [searchTerm]
  );

  useEffect(() => {
    if (!initialisedRef.current) {
      loadLeads(1, true);
      initialisedRef.current = true;
      return;
    }
    loadLeads(1, true);
  }, [loadLeads, searchTerm]);

  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  const triggerSearch = useCallback(() => {
    const trimmed = searchInput.trim();
    if (trimmed && trimmed.length < 2) {
      setMessage({ type: 'info', text: 'Enter at least two characters to search' });
      return;
    }
    if (trimmed === searchTerm) {
      loadLeads(1, true);
      return;
    }
    setSearchTerm(trimmed);
  }, [loadLeads, searchInput, searchTerm]);

  const clearSearch = useCallback(() => {
    setSearchInput('');
    if (searchTerm !== '') {
      setSearchTerm('');
    }
  }, [searchTerm]);

  const handlePageChange = useCallback(
    (targetPage: number) => {
      const nextPage = Math.min(Math.max(1, targetPage), totalPages || 1);
      if (nextPage === page) {
        return;
      }
      loadLeads(nextPage);
    },
    [loadLeads, page, totalPages]
  );

  const openCreditCheck = useCallback(
    async (lead: LeadRecord) => {
      setSelectedLead(lead);
      setCreditModalOpen(true);
      setCreditLoading(true);
      setCreditError(null);
      try {
        const summary = await leadsApi.getCreditCheckSummary(lead.id);
        setCreditSummary(summary);
      } catch (error: any) {
        console.error('Failed to fetch credit check summary', error);
        setCreditError(error?.message || 'Failed to fetch credit check summary');
      } finally {
        setCreditLoading(false);
      }
    },
    []
  );

  const openAmlSummary = useCallback(
    async (lead: LeadRecord) => {
      setSelectedLead(lead);
      setAmlModalOpen(true);
      setAmlLoading(true);
      setAmlError(null);
      try {
        const summary = await leadsApi.getAmlSummary(lead.id);
        setAmlSummary(summary);
      } catch (error: any) {
        console.error('Failed to fetch AML summary', error);
        setAmlError(error?.message || 'Failed to fetch AML summary');
      } finally {
        setAmlLoading(false);
      }
    },
    []
  );

  const handleCancelLead = useCallback(async (lead: LeadRecord) => {
    const name = [lead.firstName, lead.lastName].map((part) => (part || '').trim()).filter(Boolean).join(' ') || 'Unknown name';
    const email = lead.email?.trim() || 'no email';
    const confirmed = window.confirm(
      `Cancel this lead with ClaimsGate?\n\n${name} (${email})\n\nClaimsGate will stop reminders, scheduled transfers and credit report retrieval for this claimant.`
    );
    if (!confirmed) {
      return;
    }

    setCancellingLeadId(lead.id);
    try {
      const response = await leadsApi.cancelLead(lead.id);
      setLeads((prev) => prev.map((l) => (l.id === lead.id ? { ...l, status: 'cancelled' } : l)));
      const transferred = response.claimsAlreadyTransferred || [];
      setMessage({
        type: 'success',
        text: transferred.length > 0
          ? `Lead cancelled. ClaimsGate also cancelled already-transferred claims: ${transferred.join(', ')}`
          : 'Lead cancelled with ClaimsGate.',
      });
    } catch (error: any) {
      console.error('Failed to cancel lead', error);
      const serverError = error?.response?.data?.error;
      setMessage({ type: 'error', text: serverError || 'Failed to cancel lead with ClaimsGate' });
    } finally {
      setCancellingLeadId(null);
    }
  }, []);

  const closeCreditModal = () => {
    setCreditModalOpen(false);
    setCreditSummary(null);
    setCreditError(null);
    setCreditLoading(false);
  };

  const closeAmlModal = () => {
    setAmlModalOpen(false);
    setAmlSummary(null);
    setAmlError(null);
    setAmlLoading(false);
  };

  const sortedLeads = useMemo(() => {
    return [...leads].sort((a, b) => {
      const aDate = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const bDate = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return bDate - aDate;
    });
  }, [leads]);

  const searchButtonDisabled = isSearching || (searchInput.trim().length > 0 && searchInput.trim().length < 2);

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages: number[] = [];
    const maxPagesToShow = 7;
    let startPage = Math.max(1, page - 3);
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    if (endPage - startPage < maxPagesToShow - 1) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    for (let i = startPage; i <= endPage; i += 1) {
      pages.push(i);
    }

    return (
      <div className="pagination">
        <button className="pagination-btn" onClick={() => handlePageChange(1)} disabled={loading || page === 1}>
          First
        </button>
        <button className="pagination-btn" onClick={() => handlePageChange(page - 1)} disabled={loading || page === 1}>
          Previous
        </button>
        {pages.map((p) => (
          <button
            key={p}
            className={`pagination-btn ${p === page ? 'active' : ''}`}
            onClick={() => handlePageChange(p)}
            disabled={loading}
          >
            {p}
          </button>
        ))}
        <button
          className="pagination-btn"
          onClick={() => handlePageChange(page + 1)}
          disabled={loading || page === totalPages}
        >
          Next
        </button>
        <button
          className="pagination-btn"
          onClick={() => handlePageChange(totalPages)}
          disabled={loading || page === totalPages}
        >
          Last
        </button>
      </div>
    );
  };

  const openLeadDetails = (lead: LeadRecord) => {
    setSelectedLead(lead);
    setViewDialogOpen(true);
  };

  const closeViewDialog = () => {
    setViewDialogOpen(false);
    setSelectedLead(null);
  };

  const documentLinks = (lead: LeadRecord) => [
    { label: 'Damage Based Agreement', url: lead.damageBasedAgreementUrl, type: 'url' as const },
    { label: 'Form of Authority', url: lead.formOfAuthorityUrl, type: 'url' as const },
    { label: 'Credit Check Summary', url: lead.creditcheckUrl, type: 'credit' as const },
    { label: 'AML Check Summary', url: lead.amlcheckUrl, type: 'aml' as const },
  ];

  const amlIndividual = amlSummary?.summary?.input?.Individual as any | undefined;
  const amlAddress = amlSummary?.summary?.input?.Address as any | undefined;

  return (
    <div className="tasks-container">
      <div className="tasks-header">
        <h1>Leads</h1>
        <div className="header-controls">
          <div className="search-container" style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
            <input
              type="text"
              placeholder="Search by name, email, phone, claim ID..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  triggerSearch();
                }
              }}
              className="task-search-input"
              style={{
                padding: '8px 36px 8px 12px',
                border: '1px solid #ddd',
                borderRadius: '4px 0 0 4px',
                width: '260px',
              }}
            />
            {searchInput && (
              <button
                onClick={clearSearch}
                className="clear-search-btn"
                style={{
                  position: 'absolute',
                  right: '50px',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  fontSize: '16px',
                  color: '#666',
                }}
                title="Clear search"
              >
                ✕
              </button>
            )}
            <button
              onClick={triggerSearch}
              disabled={searchButtonDisabled}
              className="search-btn"
              style={{
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderLeft: 'none',
                borderRadius: '0 4px 4px 0',
                backgroundColor: searchButtonDisabled ? '#f0f0f0' : '#fff',
                cursor: searchButtonDisabled ? 'not-allowed' : 'pointer',
              }}
              title={searchButtonDisabled && !isSearching ? 'Enter at least two characters to search' : 'Search'}
            >
              {isSearching ? '⌛' : '🔍'}
            </button>
          </div>
          <button
            onClick={() => loadLeads(page)}
            disabled={loading}
            className="refresh-btn"
            style={{ marginLeft: '8px' }}
          >
            ↻ Refresh
          </button>
        </div>
      </div>

      {message && <div className={`global-message ${message.type}`}>{message.text}</div>}

      <div className="tasks-table-container">
        {loading && leads.length === 0 ? (
          <div className="loading-container">
            <div className="spinner" />
          </div>
        ) : (
          <>
            <table className="tasks-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Created</th>
                  <th>Lead Type</th>
                  <th>Status</th>
                  <th>Claim ID</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedLeads.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="empty-state">No leads found</td>
                  </tr>
                ) : (
                  sortedLeads.map((lead) => (
                    <tr key={lead.id}>
                      <td>{`${toDisplay(lead.title)} ${toDisplay(lead.firstName)} ${toDisplay(lead.lastName)}`.replace(/ +/g, ' ').trim()}</td>
                      <td>{toDisplay(lead.email)}</td>
                      <td>{toDisplay(lead.phoneNumber)}</td>
                      <td>{formatDateTime(lead.createdAt)}</td>
                      <td>{toDisplay(lead.leadType)}</td>
                      <td>{toDisplay(lead.status)}</td>
                      <td>{toDisplay(lead.claimId)}</td>
                      <td className="actions">
                        <button
                          className="action-btn-compact view"
                          onClick={() => openLeadDetails(lead)}
                          disabled={cancellingLeadId === lead.id}
                        >
                          View
                        </button>
                        {lead.status?.toLowerCase() !== 'cancelled' && (
                          <button
                            className="action-btn-compact cancel"
                            onClick={() => handleCancelLead(lead)}
                            disabled={!lead.userId || cancellingLeadId === lead.id}
                            title={!lead.userId ? 'No ClaimsGate userId on this lead' : 'Cancel this lead with ClaimsGate'}
                          >
                            {cancellingLeadId === lead.id ? 'Cancelling…' : 'Cancel'}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            {renderPagination()}
          </>
        )}
      </div>

      {viewDialogOpen && selectedLead && (
        <div className="modal-backdrop" onClick={closeViewDialog}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Lead details</h2>
            <div className="manual-task-content-section" style={{ marginTop: '12px' }}>
              <div className="detail-row"><strong>Created:</strong> {formatDateTime(selectedLead.createdAt)}</div>
              <div className="detail-row"><strong>Status:</strong> {toDisplay(selectedLead.status)}</div>
              <div className="detail-row"><strong>Lead type:</strong> {toDisplay(selectedLead.leadType)}</div>
              <div className="detail-row"><strong>Claim ID:</strong> {toDisplay(selectedLead.claimId)}</div>
            </div>

            <div className="manual-task-content-section" style={{ marginTop: '16px' }}>
              <h3>Contact</h3>
              <div className="detail-row"><strong>Name:</strong> {`${toDisplay(selectedLead.title)} ${toDisplay(selectedLead.firstName)} ${toDisplay(selectedLead.lastName)}`.replace(/ +/g, ' ').trim()}</div>
              <div className="detail-row"><strong>Email:</strong> {toDisplay(selectedLead.email)}</div>
              <div className="detail-row"><strong>Phone:</strong> {toDisplay(selectedLead.phoneNumber)}</div>
              <div className="detail-row"><strong>Date of birth:</strong> {toDisplay(selectedLead.dateOfBirth)}</div>
            </div>

            <div className="manual-task-content-section" style={{ marginTop: '16px' }}>
              <h3>Address</h3>
              <div className="detail-row"><strong>Address line 1:</strong> {toDisplay(selectedLead.addressLine1)}</div>
              <div className="detail-row"><strong>City:</strong> {toDisplay(selectedLead.city)}</div>
              <div className="detail-row"><strong>Postcode:</strong> {toDisplay(selectedLead.postcode)}</div>
            </div>

            <div className="manual-task-content-section" style={{ marginTop: '16px' }}>
              <h3>Documents</h3>
              <ul className="document-list">
                {documentLinks(selectedLead).map((doc) => (
                  <li key={doc.label}>
                    {doc.type === 'credit' ? (
                      doc.url ? (
                        <button className="link-button" onClick={() => openCreditCheck(selectedLead)}>
                          {doc.label}
                        </button>
                      ) : (
                        <span className="muted">{doc.label} unavailable</span>
                      )
                    ) : doc.type === 'aml' ? (
                      doc.url ? (
                        <button className="link-button" onClick={() => openAmlSummary(selectedLead)}>
                          {doc.label}
                        </button>
                      ) : (
                        <span className="muted">{doc.label} unavailable</span>
                      )
                    ) : doc.url ? (
                      <a href={doc.url} target="_blank" rel="noopener noreferrer">
                        {doc.label}
                      </a>
                    ) : (
                      <span className="muted">{doc.label} unavailable</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>

            <div className="modal-actions" style={{ marginTop: '20px' }}>
              <button className="btn-secondary" onClick={closeViewDialog}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {creditModalOpen && (
        <div className="modal-backdrop" onClick={closeCreditModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Credit Check Summary</h3>
              <button className="close-button" onClick={closeCreditModal}>&times;</button>
            </div>
            <div className="modal-body">
              {creditLoading ? (
                <p>Loading credit check data...</p>
              ) : creditError ? (
                <p style={{ color: 'red' }}>{creditError}</p>
              ) : creditSummary ? (
                <div className="credit-check-summary">
                  <div className="summary-section">
                    <h4>Summary</h4>
                    <p><strong>Provider:</strong> {creditSummary.provider || 'Unknown'}</p>
                    <p><strong>Decision:</strong> {creditSummary.summary?.overall_decision || 'N/A'}</p>
                    <p><strong>Identity verified:</strong> {creditSummary.summary?.identity_verified ? 'Yes' : 'No'}</p>
                    <p><strong>Accounts found:</strong> {creditSummary.summary?.credit_accounts_found ?? '0'}</p>
                    <p><strong>Total balance:</strong> {creditSummary.summary?.total_balance || '£0'}</p>
                    <p><strong>Worst status:</strong> {creditSummary.summary?.worst_status || 'N/A'}</p>
                    <p><strong>PEP/Sanctions clear:</strong> {creditSummary.summary?.pep_sanctions_clear ? 'Yes' : 'No'}</p>
                  </div>

                  {creditSummary.applicant && (
                    <div className="summary-section">
                      <h4>Applicant</h4>
                      <p><strong>Name:</strong> {creditSummary.applicant?.name || 'N/A'}</p>
                      <p><strong>DOB:</strong> {creditSummary.applicant?.date_of_birth || 'N/A'}</p>
                      <p><strong>Address:</strong> {creditSummary.applicant?.address || 'N/A'}</p>
                    </div>
                  )}

                  {creditSummary.electoralRoll && (
                    <div className="summary-section">
                      <h4>Electoral roll</h4>
                      <p><strong>Confirmed:</strong> {creditSummary.electoralRoll?.confirmed ? 'Yes' : 'No'}</p>
                      {creditSummary.electoralRoll?.years_at_address && (
                        <p><strong>Years at address:</strong> {creditSummary.electoralRoll.years_at_address}</p>
                      )}
                      {creditSummary.electoralRoll?.household_members?.length > 0 && (
                        <div style={{ marginTop: '8px' }}>
                          <strong>Household members:</strong>
                          <ul style={{ marginLeft: '16px', marginTop: '4px' }}>
                            {creditSummary.electoralRoll.household_members.map((member: any, index: number) => (
                              <li key={index}>
                                {member.name} {member.registered_since ? `- since ${member.registered_since}` : ''}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}

                  {creditSummary.creditAccounts && creditSummary.creditAccounts.length > 0 && (
                    <div className="summary-section">
                      <h4>Credit accounts ({creditSummary.creditAccounts.length})</h4>
                      <div style={{ maxHeight: '220px', overflowY: 'auto' }}>
                        {creditSummary.creditAccounts.map((account: any, index: number) => (
                          <div key={index} className="account-card">
                            <p><strong>{account.lender}</strong></p>
                            <p>Account: {account.account_number || 'N/A'}</p>
                            <p>Type: {account.account_type || 'N/A'}</p>
                            <p>Status: {account.status || 'N/A'}</p>
                            <p>Balance: {account.balance || 'N/A'}</p>
                            {account.start_date && <p>Start date: {account.start_date}</p>}
                            {account.settlement_date && <p>Settled: {account.settlement_date}</p>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <p>No credit check data available.</p>
              )}
            </div>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={closeCreditModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {amlModalOpen && (
        <div className="modal-backdrop" onClick={closeAmlModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>AML Check Summary</h3>
              <button className="close-button" onClick={closeAmlModal}>&times;</button>
            </div>
            <div className="modal-body">
              {amlLoading ? (
                <p>Loading AML data...</p>
              ) : amlError ? (
                <p style={{ color: 'red' }}>{amlError}</p>
              ) : amlSummary ? (
                <div className="aml-summary">
                  <div className="summary-section">
                    <h4>Summary</h4>
                    <p><strong>Status:</strong> {amlSummary.summary.status || 'N/A'}</p>
                    <p><strong>Identity result:</strong> {amlSummary.summary.identityResult || 'N/A'}</p>
                    <p><strong>Identity score:</strong> {amlSummary.summary.identityScore || 'N/A'}</p>
                    <p><strong>Match level:</strong> {amlSummary.summary.matchLevel || 'N/A'}</p>
                    <p><strong>Primary checks:</strong> {amlSummary.summary.numPrimaryChecks || 'N/A'}</p>
                    <p><strong>Corroborative checks:</strong> {amlSummary.summary.numCorroborativeChecks || 'N/A'}</p>
                  </div>

                  {amlIndividual && (
                    <div className="summary-section">
                      <h4>Input details</h4>
                      <p><strong>Name:</strong> {formatIndividualName(amlIndividual)}</p>
                      <p><strong>DOB:</strong> {amlIndividual?.Dateofbirth || 'N/A'}</p>
                      <p><strong>Phone:</strong> {amlIndividual?.Phonenumber || 'N/A'}</p>
                      <p><strong>Email:</strong> {amlIndividual?.Emailaddress || 'N/A'}</p>
                    </div>
                  )}

                  {amlAddress && (
                    <div className="summary-section">
                      <h4>Input address</h4>
                      <p><strong>Address line 1:</strong> {amlAddress?.Address1 || 'N/A'}</p>
                      <p><strong>Building number:</strong> {amlAddress?.Buildingnumber || 'N/A'}</p>
                      <p><strong>Town:</strong> {amlAddress?.Town || 'N/A'}</p>
                      <p><strong>Postcode:</strong> {amlAddress?.Postcode || 'N/A'}</p>
                    </div>
                  )}

                  {amlSummary.summary.warnings && amlSummary.summary.warnings.length > 0 && (
                    <div className="summary-section">
                      <h4>Warnings</h4>
                      <ul style={{ marginLeft: '20px' }}>
                        {amlSummary.summary.warnings.map((warning, index) => (
                          <li key={index}>
                            {warning.label}: {warning.value}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ) : (
                <p>No AML data available.</p>
              )}
            </div>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={closeAmlModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Leads;
